import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Redirect, useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { makeStyles, Typography } from '@popmenu/common-ui';
import BasicForm from '~/admin/shared/forms/BasicForm/BasicForm';
import SubmitGroup from '~/admin/shared/forms/SubmitGroup';
// @ts-expect-error component requires TS conversion
import TextFieldGroup from '~/admin/shared/forms/TextFieldGroup';
import { UpdateUserNameDocument } from '~/libs/gql/mutations/eat/updateUserNameMutation.generated';
import { useEatSession } from '../../hooks/useEatSession';
import { useReturnTo, withReturnTo } from '../../hooks/useReturnTo';
import { SignInLayout } from '../SignInLayout';

const useStyles = makeStyles(theme => ({
  help: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(-1),
  },
}));

type WelcomeFormValues = {
  firstName?: string
}

type WelcomeFormProps = {
  returnTo: string
  user: NonNullable<ReturnType<typeof useEatSession>['user']>
}

const WelcomeForm = ({ returnTo, user }: WelcomeFormProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  return (
    <BasicForm<WelcomeFormValues>
      // Social sign-up may already have filled in the name
      defaultValues={{ firstName: user.firstName ?? '' }}
      mutate={{
        mutation: UpdateUserNameDocument,
        onCompleted: () => history.replace(returnTo),
        onError: () => enqueueSnackbar(intl.formatMessage({ defaultMessage: "We couldn't save your name. Please try again." }), { variant: 'error' }),
        toVariables: ({ firstName = '' }) => ({ firstName: firstName.trim() }),
      }}
      // An unchanged name needs no save; returning false skips the mutation
      onSubmit={({ firstName = '' }) => {
        if (firstName.trim() !== (user.firstName ?? '')) return undefined;

        history.replace(returnTo);
        return false;
      }}
      suppressSnackbar
    >
      <TextFieldGroup
        field="firstName"
        title={intl.formatMessage({ defaultMessage: 'First name (optional)' })}
        variant="outlined"
      />
      <Typography className={classes.help} variant="body2">
        {intl.formatMessage({ defaultMessage: "Leave it blank and we'll use the start of your email." })}
      </Typography>
      <SubmitGroup block title={<FormattedMessage defaultMessage="Continue" />} />
    </BasicForm>
  );
};

// Phase 1 asks only for a first name; Refine your palate replaces "Continue" once it exists
export const WelcomePage = () => {
  const intl = useIntl();
  const returnTo = useReturnTo();
  const { loading, user } = useEatSession();

  // Not back to welcome: a first sign-in is sent here anyway, and anyone else doesn't need it
  if (!loading && !user) return <Redirect to={withReturnTo('/sign-in', returnTo)} />;

  return (
    <SignInLayout
      heading={<FormattedMessage defaultMessage="Welcome to Popmenu" />}
      title={intl.formatMessage({ defaultMessage: 'Welcome to Popmenu' })}
    >
      {/* Mounted once the user is known so the name field starts from their saved first name */}
      {user && <WelcomeForm returnTo={returnTo} user={user} />}
    </SignInLayout>
  );
};
